import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as BalanceIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import axios from 'axios';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Analysis() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [spendingPatterns, setSpendingPatterns] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [monthlyData, setMonthlyData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [spendingResponse, recommendationsResponse] = await Promise.all([
              fetch('https://stu148664.webhosting.arden.ac.uk/COM6008/api/analysis/spending-patterns'),
      fetch('https://stu148664.webhosting.arden.ac.uk/COM6008/api/analysis/recommendations')
      ]);

      if (!spendingResponse.ok || !recommendationsResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const spendingData = await spendingResponse.json();
      const recommendationsData = await recommendationsResponse.json();

      setSpendingPatterns(spendingData);
      setRecommendations(Array.isArray(recommendationsData) ? recommendationsData : []);
      processData(spendingData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch analysis data');
    } finally {
      setLoading(false);
    }
  };

  const processData = (data) => {
    if (!data) return;

    // Process monthly trends data
    const monthlyData = data.monthly_trends || [];
    const labels = monthlyData.map(item => item.month);
    const incomeData = monthlyData.map(item => item.income || 0);
    const expenseData = monthlyData.map(item => item.expenses || 0);

    setMonthlyData({
      labels,
      datasets: [
        {
          label: 'Income',
          data: incomeData,
          borderColor: theme.palette.success.main,
          backgroundColor: theme.palette.success.light,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: theme.palette.success.main,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
        {
          label: 'Expenses',
          data: expenseData,
          borderColor: theme.palette.error.main,
          backgroundColor: theme.palette.error.light,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: theme.palette.error.main,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    });

    // Process category distribution data
    const categoryData = data.category_distribution || [];
    setCategoryData({
      labels: categoryData.map(item => item.category),
      datasets: [
        {
          data: categoryData.map(item => item.amount),
          backgroundColor: [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.error.main,
            theme.palette.warning.main,
            theme.palette.info.main,
            theme.palette.success.main,
          ],
        },
      ],
    });

    // Generate insights based on the data
    const insights = [];
    const summary = data.summary || { total_income: 0, total_expenses: 0, balance: 0 };

    if (summary.total_expenses > summary.total_income) {
      insights.push({
        type: 'warning',
        message: `You spent $${Math.abs(summary.balance).toFixed(2)} more than you earned this month.`
      });
    }

    if (categoryData.length > 0) {
      const highestCategory = categoryData.reduce((max, current) => 
        current.amount > max.amount ? current : max
      );
      insights.push({
        type: 'info',
        message: `Your highest spending category is ${highestCategory.category} ($${highestCategory.amount.toFixed(2)})`
      });
    }

    setInsights(insights);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 2 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold', color: theme.palette.primary.main }}>
        Financial Analysis
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Total Income</Typography>
              </Box>
              <Typography variant="h4">
                ${spendingPatterns?.summary.total_income.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Total Expenses</Typography>
              </Box>
              <Typography variant="h4">
                ${spendingPatterns?.summary.total_expenses.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BalanceIcon sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Balance</Typography>
              </Box>
              <Typography variant="h4">
                ${spendingPatterns?.summary.balance.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Monthly Trends */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Monthly Trends
            </Typography>
            {monthlyData && (
              <Box sx={{ height: 400 }}>
                <Line
                  data={monthlyData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: theme.palette.divider,
                        },
                        ticks: {
                          color: theme.palette.text.primary,
                        },
                        title: {
                          color: theme.palette.text.primary,
                        },
                      },
                      x: {
                        grid: {
                          color: theme.palette.divider,
                        },
                        ticks: {
                          color: theme.palette.text.primary,
                        },
                        title: {
                          color: theme.palette.text.primary,
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          color: theme.palette.text.primary,
                        },
                      },
                      tooltip: {
                        backgroundColor: theme.palette.background.paper,
                        titleColor: theme.palette.text.primary,
                        bodyColor: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                        borderWidth: 1,
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Category Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Spending by Category
            </Typography>
            {categoryData && (
              <Box sx={{ height: 300 }}>
                <Pie
                  data={categoryData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: {
                          color: theme.palette.text.primary,
                        },
                      },
                      tooltip: {
                        backgroundColor: theme.palette.background.paper,
                        titleColor: theme.palette.text.primary,
                        bodyColor: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                        borderWidth: 1,
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Key Insights */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Insights
              </Typography>
              {insights.length > 0 ? (
                <List>
                  {insights.map((insight, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {insight.type === 'warning' ? (
                          <WarningIcon color="warning" />
                        ) : (
                          <InfoIcon color="info" />
                        )}
                      </ListItemIcon>
                      <ListItemText primary={insight.message} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Add more transactions to get personalized insights.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recommendations */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personalized Recommendations
              </Typography>
              {recommendations.length > 0 ? (
                <List>
                  {recommendations.map((rec, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {rec.type === 'warning' ? (
                          <WarningIcon color="warning" />
                        ) : rec.type === 'suggestion' ? (
                          <LightbulbIcon color="primary" />
                        ) : (
                          <InfoIcon color="info" />
                        )}
                      </ListItemIcon>
                      <ListItemText primary={rec.message} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Add more transactions to get personalized recommendations.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Analysis; 